import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, formatApiError } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AUTH } from '@/constants/testIds';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left panel - architectural imagery */}
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(10,37,64,0.72) 0%, rgba(10,37,64,0.88) 100%), url(https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80)',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between text-white">
          <Logo variant="wordmark" className="w-full h-auto block" alt="STEELPEAK" />
          <div className="p-14 pt-0">
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              Institutional knowledge, distilled with precision.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              A private retrieval-augmented intelligence layer for STEELPEAK.
            </p>
            <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50">
              <div className="h-px w-8 bg-[#D4AF37]" />
              <span>STPK RAG</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="relative flex items-center justify-center px-6 py-16 lg:px-16 bg-[#FAFAFA]">
        <div className="absolute top-6 right-6"><ThemeToggle /></div>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" className="h-7 w-auto rounded" alt="STEELPEAK" />
          </div>

          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
              Members Only
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
              Welcome back
            </h1>
            <p className="mt-3 text-sm text-slate-500">
              Sign in to access the STEELPEAK knowledge base.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid={AUTH.loginForm}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.loginEmail}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="you@spwm.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.loginPassword}
                className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.authError}
                className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid={AUTH.loginSubmit}
              className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide"
            >
              {submitting ? 'Signing in…' : 'Sign In'}
            </Button>

            <div className="text-right -mt-2">
              <Link
                to="/forgot-password"
                data-testid={AUTH.forgotLink}
                className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#0A2540] transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-sm text-slate-500 text-center">
            Don't have an account?{' '}
            <Link
              to="/register"
              data-testid={AUTH.loginToRegister}
              className="text-[#0A2540] font-medium hover:text-[#D4AF37] underline underline-offset-4"
            >
              Request access
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
